import { Fragment, useSyncExternalStore, type ReactNode } from "react";
import { DiscardEditsModal } from "./DiscardEditsModal";
import { WindowTaskbar } from "./WindowTaskbar";
import type { WindowEntry, WindowStackManager } from "./WindowStack";

type WindowStackHostProps = {
  stack: WindowStackManager;
  // kind→콘텐츠 렌더. 호스트는 kind 를 모르고 소비처가 분기한다. active 면 전체화면, 아니면 최소화.
  renderWindow: (entry: WindowEntry, active: boolean) => ReactNode;
};

// 전체화면 창 스택 호스트 — 모든 엔트리를 항상 마운트(콘텐츠 상태 보존)하고 활성 하나만 전체화면으로
// 보인다. 창이 하나라도 있으면 상단 작업표시줄을 항상 띄워(활성 창이 떠 있어도) 어떤 창들이 열려 있는지
// 보여준다. 닫기 guard 가 닫기를 멈추면 확인 Modal 을 띄운다. 스택을 구독할 뿐 콘텐츠/도메인은 모른다.
export function WindowStackHost({ stack, renderWindow }: WindowStackHostProps): ReactNode {
  const { entries, activeId, closeRequest } = useSyncExternalStore(
    stack.subscribe,
    stack.getSnapshot,
  );
  const closing = entries.find((entry) => entry.id === closeRequest) ?? null;

  // 작업표시줄 본문 클릭: 활성 항목이면 최소화(배경으로 토글), 아니면 그 창을 전체화면으로 전환한다.
  const handleSelect = (id: string): void => {
    if (id === activeId) {
      stack.minimize(id);
    } else {
      stack.setActive(id);
    }
  };

  return (
    <>
      {entries.map((entry) => (
        // entry.id 로 key 를 줘 창마다 콘텐츠가 새로 생기고, 최소화에도 mount 가 유지된다.
        <Fragment key={entry.id}>{renderWindow(entry, entry.id === activeId)}</Fragment>
      ))}
      {entries.length > 0 ? (
        <WindowTaskbar
          entries={entries}
          activeId={activeId}
          onSelect={handleSelect}
          onClose={stack.requestClose}
        />
      ) : null}
      {closing === null ? null : (
        <DiscardEditsModal
          heading="창 닫기"
          message={`${closing.title} 창에 저장하지 않은 편집이 있습니다. 창을 닫으면 편집을 잃습니다.`}
          confirmText="편집 버리고 닫기"
          onCancel={stack.cancelClose}
          onConfirm={stack.confirmClose}
        />
      )}
    </>
  );
}
